import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Trash2, Search, UserPlus, Lock, Power, Copy, X, Check, Shield } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import OCNPermissionDialog from '../components/OCNPermissionDialog';

export const canManageOcnAccess = (userRole, ocnEnabled) =>
  (userRole === 'super_admin' || userRole === 'customer_admin') && ocnEnabled;

const ROLES = [
  { value: 'super_admin',          label: 'Super Admin',                    color: 'yellow' },
  { value: 'customer_admin',       label: 'Kundadmin',                      color: 'blue'   },
  { value: 'employee',             label: 'Medarbetare',                    color: 'green'  },
  { value: 'read_only_assigner',   label: 'Läsbehörighet (Uppdragsgivare)', color: 'gray'   },
  { value: 'read_only_participant', label: 'Läsbehörighet (Deltagare)',     color: 'gray'   },
];

const COLOR_MAP = {
  yellow: { gradient: 'from-[#F9CF3F] to-[#e6ba38]', badge: 'bg-primary/20 text-[#333333]', stat: 'bg-primary/20 text-[#333333]' },
  blue:   { gradient: 'from-blue-400 to-blue-600',   badge: 'bg-blue-100 text-blue-700',   stat: 'bg-blue-100 text-blue-600'   },
  green:  { gradient: 'from-green-400 to-green-600', badge: 'bg-green-100 text-green-700', stat: 'bg-green-100 text-green-600' },
  gray:   { gradient: 'from-gray-400 to-gray-600',   badge: 'bg-gray-100 text-gray-700',   stat: 'bg-gray-100 text-gray-600'   },
};

const selectCls = 'h-9 w-full px-3 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring';

const getRoleMeta = (role) => ROLES.find(r => r.value === role) || { label: role, color: 'gray' };

const formatDate = (iso) => {
  if (!iso) return 'Aldrig';
  return new Date(iso).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' });
};

const UsersAdminPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter]   = useState('all');

  // Invite form
  const [showInvite, setShowInvite]   = useState(false);
  const [inviteForm, setInviteForm]   = useState({
    email: '', name: '', role: 'employee', org_id: '',
    linked_participant_id: '',
    linked_participant_ids: [],
  });
  const [inviting, setInviting]       = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [copied, setCopied]           = useState(false);
  const [customers, setCustomers]     = useState([]);

  // Inline edit state
  const [editingUserId, setEditingUserId] = useState(null);
  const [editForm, setEditForm]           = useState({ name: '', role: '', participant_id: '' });
  const [editSaving, setEditSaving]       = useState(false);
  const [participants, setParticipants]   = useState([]);

  // Password dialog
  const [pwUser, setPwUser]       = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [pwSaving, setPwSaving]   = useState(false);

  // OCN
  const [ocnEnabled, setOcnEnabled]           = useState(false);
  const [ocnDialogUser, setOcnDialogUser]     = useState(null);
  const [ocnBannerDismissed, setOcnBannerDismissed] = useState(false);

  const isSuperAdmin  = currentUser?.role === 'super_admin';
  const isCustomerAdmin = currentUser?.role === 'customer_admin';
  const canManageOcn  = canManageOcnAccess(currentUser?.role, ocnEnabled);

  // Escape closes inline edit
  useEffect(() => {
    if (!editingUserId) return;
    const handler = (e) => { if (e.key === 'Escape') setEditingUserId(null); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [editingUserId]);

  useEffect(() => {
    fetchUsers();
    if (isSuperAdmin) {
      setOcnEnabled(true);
      api.get('/clients')
        .then(r => setCustomers(r.data || []))
        .catch(() => toast.error('Kunde inte hämta organisationer'));
    } else if (isCustomerAdmin) {
      api.get('/org/subscription')
        .then(r => setOcnEnabled(r.data?.ocn?.enabled === true))
        .catch(() => {});
    }
  }, [isSuperAdmin, isCustomerAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data || []);
    } catch {
      toast.error('Kunde inte hämta användare');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Invite ─────────────────────────────────────────────────────────────────

  const ensureParticipantsLoaded = () => {
    if (participants.length === 0) {
      api.get('/participants')
        .then(r => setParticipants(r.data || []))
        .catch(() => {});
    }
  };

  const handleInviteRoleChange = (role) => {
    setInviteForm(f => ({
      ...f,
      role,
      linked_participant_id: '',
      linked_participant_ids: [],
    }));
    if (role === 'read_only_participant' || role === 'read_only_assigner') {
      ensureParticipantsLoaded();
    }
  };

  const toggleAssignerParticipant = (pid) => {
    setInviteForm(f => {
      const current = f.linked_participant_ids || [];
      const next = current.includes(pid)
        ? current.filter(id => id !== pid)
        : [...current, pid];
      return { ...f, linked_participant_ids: next };
    });
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (inviteForm.role === 'read_only_participant' && !inviteForm.linked_participant_id) {
      toast.error('Välj vilken deltagare som ska kopplas');
      return;
    }
    if (inviteForm.role === 'read_only_assigner' && inviteForm.linked_participant_ids.length === 0) {
      toast.error('Välj minst en deltagare');
      return;
    }
    setInviting(true);
    try {
      const payload = {
        email: inviteForm.email,
        name: inviteForm.name,
        role: inviteForm.role,
        ...(isSuperAdmin && inviteForm.org_id ? { org_id: inviteForm.org_id } : {}),
        ...(inviteForm.role === 'read_only_participant'
          ? { linked_participant_id: inviteForm.linked_participant_id }
          : {}),
        ...(inviteForm.role === 'read_only_assigner'
          ? { linked_participant_ids: inviteForm.linked_participant_ids }
          : {}),
      };
      const response = await api.post('/users/invite', payload);
      setUsers(prev => [...prev, response.data.user]);
      setTempPassword(response.data.temp_password);
      setInviteForm({
        email: '', name: '', role: 'employee', org_id: '',
        linked_participant_id: '', linked_participant_ids: [],
      });
      setShowInvite(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte bjuda in användaren');
    } finally {
      setInviting(false);
    }
  };

  const copyTempPassword = () => {
    navigator.clipboard.writeText(tempPassword).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Inline edit ────────────────────────────────────────────────────────────

  const openEdit = (user) => {
    setEditingUserId(user.id);
    setEditForm({
      name: user.name,
      role: user.role,
      participant_id: user.participant_id || '',
    });
    if (user.role === 'read_only_participant' && participants.length === 0) {
      api.get('/participants')
        .then(r => setParticipants(r.data || []))
        .catch(() => {});
    }
  };

  const handleEditRoleChange = (role) => {
    setEditForm(f => ({
      ...f,
      role,
      participant_id: role === 'read_only_participant' ? f.participant_id : '',
    }));
    if (role === 'read_only_participant' && participants.length === 0) {
      api.get('/participants')
        .then(r => setParticipants(r.data || []))
        .catch(() => {});
    }
  };

  const handleEdit = async (userId) => {
    if (!editForm.name.trim()) {
      toast.error('Namn krävs');
      return;
    }
    setEditSaving(true);
    try {
      const payload = { name: editForm.name, role: editForm.role };
      if (editForm.role === 'read_only_participant') {
        payload.participant_id = editForm.participant_id || null;
      }
      const response = await api.put(`/users/${userId}`, payload);
      setUsers(prev => prev.map(u => u.id === userId ? response.data : u));
      toast.success('Användare uppdaterad');
      setEditingUserId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte uppdatera användaren');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Toggle status ──────────────────────────────────────────────────────────

  const handleToggleStatus = async (user) => {
    try {
      const response = await api.put(`/users/${user.id}/status`, { is_active: !user.is_active });
      setUsers(prev => prev.map(u => u.id === user.id ? response.data : u));
      toast.success(response.data.is_active ? 'Användare aktiverad' : 'Användare inaktiverad');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte ändra status');
    }
  };

  // ── Password ───────────────────────────────────────────────────────────────

  const openPassword = (user) => { setPwUser(user); setNewPassword(''); };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwSaving(true);
    try {
      await api.put(`/users/${pwUser.id}/password`, { password: newPassword });
      toast.success('Lösenord uppdaterat');
      setPwUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte uppdatera lösenordet');
    } finally {
      setPwSaving(false);
    }
  };

  // ── OCN ────────────────────────────────────────────────────────────────────

  const handleOcnUpdated = (updatedUser) => {
    setUsers(prev => prev.map(u =>
      u.id === updatedUser.id ? { ...u, ocn_access: updatedUser.ocn_access } : u
    ));
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async (user) => {
    if (!window.confirm(`Radera ${user.name}? Åtgärden kan inte ångras.`)) return;
    try {
      await api.delete(`/users/${user.id}`);
      setUsers(prev => prev.filter(u => u.id !== user.id));
      toast.success('Användare raderad');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte radera användaren');
    }
  };

  // ── Filter ─────────────────────────────────────────────────────────────────

  const filteredUsers = users.filter(u => {
    const matchesSearch =
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const assignableRoles = isSuperAdmin ? ROLES : ROLES.filter(r => r.value !== 'super_admin');

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="users-admin-page">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight flex items-center gap-3">
              <Users className="text-primary" size={36} />
              Användare
            </h1>
            <p className="text-muted-foreground mt-2">Användarhantering och rollstyrning</p>
          </div>
          <Button
            onClick={() => setShowInvite(v => !v)}
            className="rounded-full font-bold gap-2"
          >
            {showInvite ? <X size={18} /> : <UserPlus size={18} />}
            {showInvite ? 'Avbryt' : 'Bjud in användare'}
          </Button>
        </div>

        {/* OCN banner — shown when OCN is not enabled and not dismissed */}
        {isCustomerAdmin && !ocnEnabled && !ocnBannerDismissed && (
          <div
            data-testid="ocn-banner"
            className="flex items-start justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
          >
            <div className="flex items-center gap-2">
              <Shield size={16} className="shrink-0 mt-0.5" />
              <span>
                OCN är inte aktiverat för denna organisation.
                Kontakta plattformsadministratören för att aktivera OCN.
              </span>
            </div>
            <button
              onClick={() => setOcnBannerDismissed(true)}
              aria-label="Stäng"
              className="shrink-0 text-blue-500 hover:text-blue-700"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Invite form */}
        {showInvite && (
          <Card className="border-primary/30 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading font-bold text-lg">Bjud in ny användare</CardTitle>
              <p className="text-sm text-muted-foreground">
                Inget e-postmeddelande skickas — ett temporärt lösenord visas när kontot skapas.
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInvite} className="flex flex-wrap gap-4 items-end">
                <div className="space-y-1 flex-1 min-w-[180px]">
                  <Label htmlFor="invite-name">Namn</Label>
                  <Input
                    id="invite-name"
                    placeholder="Anna Svensson"
                    value={inviteForm.name}
                    onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-1 flex-1 min-w-[200px]">
                  <Label htmlFor="invite-email">E-postadress</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="anna@example.com"
                    value={inviteForm.email}
                    onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-1 w-48">
                  <Label htmlFor="invite-role">Roll</Label>
                  <select
                    id="invite-role"
                    data-testid="invite-role"
                    value={inviteForm.role}
                    onChange={e => handleInviteRoleChange(e.target.value)}
                    className={selectCls}
                  >
                    {assignableRoles.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                {isSuperAdmin && (
                  <div className="space-y-1 flex-1 min-w-[200px]">
                    <Label htmlFor="invite-org">Organisation</Label>
                    <Select
                      value={inviteForm.org_id || undefined}
                      onValueChange={v => setInviteForm(f => ({ ...f, org_id: v }))}
                    >
                      <SelectTrigger id="invite-org">
                        <SelectValue placeholder="Ingen org (lämna tomt)" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-muted-foreground">
                            Inga organisationer skapade ännu
                          </div>
                        ) : customers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.namn}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {inviteForm.role === 'read_only_participant' && (
                  <div className="space-y-1 w-full" data-testid="invite-participant-block">
                    <Label htmlFor="invite-participant">Koppla till deltagare</Label>
                    <select
                      id="invite-participant"
                      data-testid="invite-linked-participant"
                      value={inviteForm.linked_participant_id}
                      onChange={e => setInviteForm(f => ({ ...f, linked_participant_id: e.target.value }))}
                      className={selectCls}
                      required
                    >
                      <option value="">— Välj deltagare —</option>
                      {participants.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {inviteForm.role === 'read_only_assigner' && (
                  <div className="space-y-1 w-full" data-testid="invite-assigner-block">
                    <Label>Koppla till deltagare (minst en)</Label>
                    <div className="border border-input rounded-md max-h-48 overflow-y-auto p-2 space-y-1 bg-background">
                      {participants.length === 0 ? (
                        <p className="text-xs text-muted-foreground p-2">Inga deltagare i organisationen ännu.</p>
                      ) : participants.map(p => (
                        <label key={p.id} className="flex items-center gap-2 text-sm hover:bg-muted/40 rounded px-2 py-1 cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid={`invite-assigner-checkbox-${p.id}`}
                            checked={inviteForm.linked_participant_ids.includes(p.id)}
                            onChange={() => toggleAssignerParticipant(p.id)}
                          />
                          <span>{p.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Valda: {inviteForm.linked_participant_ids.length}
                    </p>
                  </div>
                )}
                <Button type="submit" disabled={inviting} className="rounded-full font-bold">
                  {inviting ? 'Skapar...' : 'Skapa konto'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Temp password dialog */}
        <Dialog open={!!tempPassword} onOpenChange={() => { setTempPassword(null); setCopied(false); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">Konto skapat</DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground text-sm">
              Spara det temporära lösenordet och ge det till användaren. Det visas bara en gång.
            </p>
            <div className="flex items-center gap-2 bg-muted rounded-lg p-4 font-mono text-lg">
              <span className="flex-1 break-all">{tempPassword}</span>
              <button
                onClick={copyTempPassword}
                className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                title="Kopiera"
              >
                {copied ? <Check size={20} className="text-green-600" /> : <Copy size={20} />}
              </button>
            </div>
            <Button onClick={() => { setTempPassword(null); setCopied(false); }} className="w-full rounded-full font-bold">
              Jag har sparat lösenordet
            </Button>
          </DialogContent>
        </Dialog>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {ROLES.map(role => {
            const count  = users.filter(u => u.role === role.value).length;
            const colors = COLOR_MAP[role.color];
            return (
              <Card key={role.value}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{role.label}</p>
                      <p className="text-2xl font-bold mt-1">{count}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${colors.stat}`}>
                      <Users size={22} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Search + filter */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder="Sök på namn eller e-post..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-48"><SelectValue placeholder="Filtrera roll" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla roller</SelectItem>
              {ROLES.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* User cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <Card className="col-span-full">
              <CardContent className="p-6 text-muted-foreground">Laddar användare...</CardContent>
            </Card>
          ) : filteredUsers.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="p-12 text-center">
                <Users size={64} className="mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground text-lg">
                  {users.length === 0
                    ? 'Inga användare ännu. Bjud in din första medarbetare.'
                    : 'Inga användare matchar din sökning.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredUsers.map(user => {
              const meta   = getRoleMeta(user.role);
              const colors = COLOR_MAP[meta.color];
              const isMe        = user.id === currentUser?.id;
              const isProtected = !isSuperAdmin && user.role === 'super_admin';
              const isEditing   = editingUserId === user.id;

              return (
                <Card key={user.id} data-testid={`user-card-${user.id}`} className="hover:shadow-lg transition-shadow">
                  <div className={`h-20 bg-gradient-to-br ${colors.gradient} rounded-t-lg flex items-center justify-center`}>
                    <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center text-xl font-bold text-gray-700">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <CardContent className="pt-4 space-y-3">

                    {isEditing ? (
                      /* ── Inline edit form ── */
                      <div data-testid={`user-edit-form-${user.id}`} className="space-y-3">
                        <div className="space-y-1.5">
                          <Label htmlFor={`edit-name-${user.id}`}>Namn *</Label>
                          <Input
                            id={`edit-name-${user.id}`}
                            data-testid={`user-edit-name-${user.id}`}
                            value={editForm.name}
                            onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`edit-role-${user.id}`}>Roll</Label>
                          <select
                            id={`edit-role-${user.id}`}
                            data-testid={`user-edit-role-${user.id}`}
                            value={editForm.role}
                            onChange={e => handleEditRoleChange(e.target.value)}
                            className={selectCls}
                          >
                            {assignableRoles.map(r => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        </div>
                        {editForm.role === 'read_only_participant' && (
                          <div className="space-y-1.5">
                            <Label htmlFor={`edit-participant-${user.id}`}>Kopplad deltagare</Label>
                            <select
                              id={`edit-participant-${user.id}`}
                              data-testid={`user-edit-participant-${user.id}`}
                              value={editForm.participant_id}
                              onChange={e => setEditForm(f => ({ ...f, participant_id: e.target.value }))}
                              className={selectCls}
                            >
                              <option value="">— Ingen koppling —</option>
                              {participants.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="flex gap-2 pt-1">
                          <Button
                            size="sm"
                            data-testid={`user-edit-save-${user.id}`}
                            onClick={() => handleEdit(user.id)}
                            disabled={editSaving}
                            className="flex-1 rounded-full font-bold text-xs"
                          >
                            {editSaving ? 'Sparar...' : 'Spara'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`user-edit-cancel-${user.id}`}
                            onClick={() => setEditingUserId(null)}
                            className="rounded-full text-xs"
                          >
                            Avbryt
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* ── Normal card content ── */
                      <>
                        <div className="text-center">
                          <h3 className="font-bold text-base leading-tight">{user.name}</h3>
                          <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                        </div>

                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${colors.badge}`}>
                            {meta.label}
                          </span>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {user.is_active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                          {ocnEnabled && user.role === 'employee' && (
                            <span
                              data-testid={`ocn-badge-${user.id}`}
                              className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${user.ocn_access ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                            >
                              {user.ocn_access ? 'OCN: Aktiv' : 'OCN: Ej tilldelad'}
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-center text-muted-foreground">
                          Senast inloggad: {formatDate(user.last_login_at)}
                        </p>

                        {isMe ? (
                          <div className="text-center pt-3 border-t">
                            <span className="text-xs text-muted-foreground">Det här är du</span>
                          </div>
                        ) : isProtected ? (
                          <div className="text-center pt-3 border-t">
                            <span className="text-xs text-muted-foreground">Systemadministratör</span>
                          </div>
                        ) : (
                          <div className="flex gap-1.5 pt-3 border-t flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`edit-user-btn-${user.id}`}
                              onClick={() => openEdit(user)}
                              className="flex-1 text-xs gap-1"
                              title="Redigera"
                            >
                              Redigera
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPassword(user)}
                              className="text-xs gap-1 px-2"
                              title="Byt lösenord"
                            >
                              <Lock size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleStatus(user)}
                              className={`text-xs gap-1 px-2 ${user.is_active ? 'text-orange-600 hover:bg-orange-50' : 'text-green-600 hover:bg-green-50'}`}
                              title={user.is_active ? 'Inaktivera' : 'Aktivera'}
                            >
                              <Power size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(user)}
                              className="text-xs px-2 text-red-600 hover:bg-red-50"
                              title="Radera"
                            >
                              <Trash2 size={14} />
                            </Button>
                            {(isSuperAdmin || isCustomerAdmin) && user.role === 'employee' && (
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`ocn-manage-btn-${user.id}`}
                                onClick={() => canManageOcn && setOcnDialogUser(user)}
                                disabled={!canManageOcn}
                                title={!canManageOcn ? 'OCN ej aktiverat för organisationen' : 'Hantera OCN-behörighet'}
                                className="text-xs gap-1 px-2"
                              >
                                <Shield size={14} />
                              </Button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* OCN permission dialog */}
        <OCNPermissionDialog
          user={ocnDialogUser}
          open={!!ocnDialogUser}
          onClose={() => setOcnDialogUser(null)}
          onUpdated={handleOcnUpdated}
        />

        {/* Password dialog — stays as modal (security context) */}
        <Dialog open={!!pwUser} onOpenChange={open => { if (!open) setPwUser(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading font-bold">Byt lösenord</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Sätt ett nytt lösenord för <strong>{pwUser?.name}</strong>.
            </p>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">Nytt lösenord (minst 8 tecken)</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </div>
              <Button type="submit" disabled={pwSaving} className="w-full rounded-full font-bold">
                {pwSaving ? 'Sparar...' : 'Uppdatera lösenord'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
};

export default UsersAdminPage;
